import React from 'react'
import { format } from '@fs/zion-locale/date-fns'
import { useFeatureFlag } from '@fs/zion-flags'
import { useTranslation } from 'react-i18next'
import { conferenceConfig } from '@fs/zion-rootstech'
import { Badge } from './Badge'
import { getI18nDate, getNowTimestamp } from './helpers/dateTimeHelpers'
import { day } from '../config'
import { MonoText } from './type'
// import { propTypes as brightspot } from '../api/brightspot'

const sevenDays = 7 * day

export default function Timestamp({
  timestamp,
  conferenceYear,
  isActive = false,
}: {
  timestamp?: string | null
  conferenceYear?: string | null
  isActive?: boolean
}): JSX.Element | null {
  // short-circuit to simplify the logic
  const scheduleFlag = useFeatureFlag('rootstech_schedule')
  const [t] = useTranslation()

  // Clean data from Brightspot - trim whitespace and parse to number
  const cleanTimestamp: number | null = timestamp ? Number(String(timestamp).trim()) : null
  const cleanConferenceYear = conferenceYear ? String(conferenceYear).trim() : conferenceYear

  if ((!cleanTimestamp || cleanTimestamp < 0) && !cleanConferenceYear) {
    return <div />
  }
  const conferenceEndTimestamp = conferenceConfig.inPerson.ends
  const conferenceStartTimestamp = conferenceConfig.inPerson.starts
  const nowTimestamp = getNowTimestamp()
  const isLive =
    cleanConferenceYear && // is conference year present
    Number(cleanConferenceYear) >= new Date(conferenceEndTimestamp).getFullYear() && // is the conference year the current year or in the future?
    nowTimestamp < new Date(conferenceEndTimestamp).getTime() // are we currently before the conference end timestamp?

  // if a timestamp was provided use it, otherwise fall back to conference start
  const finalTimestamp = cleanTimestamp || new Date(conferenceStartTimestamp).getTime()

  /**
   * 1. if it's in the past...
   *   a. don't show the time, timezone, or weekday.
   *   b. show the year
   * 2. if it's in the future and...
   *   a. it's in the next 7 days, show the weekday, time, and timezone.
   *   b. it's further out than the next seven days, show the month, day, year, time, and timezone
   */
  const formatOptions = {
    time: isLive || isActive,
    timezone: isLive || isActive,
    weekday: (isLive || isActive) && finalTimestamp - nowTimestamp < sevenDays,
    year: !isLive || `${new Date(finalTimestamp).getFullYear()}` !== cleanConferenceYear,
  }

  const showTimestamp = isActive || (finalTimestamp > nowTimestamp && scheduleFlag.isOn) // currently running or in the future

  const label =
    showTimestamp && finalTimestamp ? (
      <time dateTime={format(finalTimestamp, 'yyyy-MM-dd h:MM')}>{getI18nDate(finalTimestamp, formatOptions)}</time>
    ) : (
      cleanConferenceYear
    )
  const testId = showTimestamp && finalTimestamp ? 'timestamp' : 'year'
  const ariaLabel =
    showTimestamp && finalTimestamp
      ? t('badge.timestamp', 'Session is available on {timestamp}', {
          timestamp: getI18nDate(finalTimestamp, formatOptions),
        })
      : t('badge.year', 'Session was published in {year}', { year: cleanConferenceYear })

  return label ? (
    // This div needs to stay so the outside Row can growX if needed
    <div>
      <Badge
        data-testid={`item-${testId}`}
        flavor="flavorless"
        keyline="left"
        label={<MonoText complementary={false}>{label}</MonoText>}
        aria-label={ariaLabel}
      />
    </div>
  ) : null
}
